import React from 'react';
import { arrayMoveImmutable } from 'array-move';
import { FormattedMessage } from 'react-intl';

import { Empty, Row, Col, Button } from 'antd';
import { DeleteFilled } from '@ant-design/icons';
import { dayjs } from 'shared/utils/date';
import DateRangePicker from 'components/DateRangePicker';
import Collapse from 'components/SortableCollapse';
import ResultContent from 'containers/Results/ResultsPage/ResultContent';
import { Link } from 'react-router-dom';
import messages from '../messages';

const { Panel } = Collapse;

// Made into a component in order to for it to be wrapped in a form field
const ReportContent = ({
  reportDateRange,
  timeOffset,
  language,
  content,
  onChange,
}) => {
  const handleChange = (chart) => {
    const updated = content.filter(c => c.id !== chart.id);
    const i = content.findIndex(c => c.id === chart.id);
    updated.splice(i, 0, chart);

    onChange(updated);
  };

  const onChartDateRangeChanged = chart => (dateRange) => {
    const chartCopy = JSON.parse(JSON.stringify(chart)); // deep copy
    if (!chartCopy.filter) {
      chartCopy.filter = {};
    }
    if (dateRange === reportDateRange) {
      chartCopy.filter.dateRange = null;
    } else {
      chartCopy.filter.dateRange = dateRange;
    }

    handleChange(chartCopy);
  };

  const onChartSettingsChanged = chart => (settings) => {
    const chartCopy = JSON.parse(JSON.stringify(chart)); // deep copy
    chartCopy.chartSettings = { ...chartCopy.chartSettings, ...settings };

    handleChange(chartCopy);
  };

  const onContentSettingsChanged = chart => (settings) => {
    const chartCopy = JSON.parse(JSON.stringify(chart)); // Deep copy
    chartCopy.contentSettings = { ...chartCopy.contentSettings, ...settings };

    handleChange(chartCopy);
  };

  const move = ({ oldIndex, newIndex }) => {
    const sorted = arrayMoveImmutable(content, oldIndex, newIndex);
    onChange(sorted);
  };

  const handleDelete = id => (e) => {
    e.stopPropagation();
    onChange(content.filter(c => c.id !== id));
  };

  const today = dayjs().startOf('day');

  if (!content.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={<FormattedMessage {...messages.reportContentEmptyMessage} />}
      />
    );
  }

  return (
    <Collapse onSortEnd={move}>
      {content.map((item, index) => (
        <Panel
          index={index}
          key={item.id}
          header={item.title}
          extra={(
            <Button
              size="small"
              type="link"
              danger
              icon={<DeleteFilled />}
              onClick={handleDelete(item.id)}
            />
          )}
        >
          <Row gutter={16} type="flex" align="middle" style={{ marginBottom: '1em' }}>
            <Col span={8}>
              <DateRangePicker
                defaultValue={reportDateRange}
                value={item.filter?.dateRange}
                onChange={onChartDateRangeChanged(item)}
                placeholder={<FormattedMessage {...messages.reportContentDateRangePlaceholder} />}
              />
            </Col>
            <Col span={16} style={{ textAlign: 'right' }}>
              {item.filter?.surveyGroupId && (
              <Link to={`/surveys/${item.filter.surveyGroupId}`}><Button>Go to survey</Button></Link>
              )}
            </Col>
          </Row>
          <ResultContent
            viewDate={today}
            dateRange={item.filter?.dateRange || reportDateRange}
            surveyGroupId={item.filter?.surveyGroupId}
            dataSourceId={item.filter?.dataSource?.id}
            filterSegments={item.filter?.filterSegments}
            benchmarkSegments={item.filter?.benchmarkSegments}
            contentSettings={item.contentSettings}
            onContentSettingsChanged={onContentSettingsChanged(item)}
            chartSettings={item.chartSettings}
            onChartSettingsChanged={onChartSettingsChanged(item)}
            timeOffset={timeOffset}
            language={language}
            isReport
          />
        </Panel>
      ))}
    </Collapse>
  );
};

export default ReportContent;
